// src/pages/CannedResponses.jsx
import React, { useEffect, useState, useContext } from 'react';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { Form, Button, Spinner, Modal, Badge } from 'react-bootstrap';

// ── Design tokens ─────────────────────────────────────────────────────────────
const CARD  = { background: 'var(--c-card)', borderRadius: 12, border: '1px solid var(--c-border)', boxShadow: '0 1px 4px rgba(0,0,0,.06)' };
const LABEL = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--c-text-4)', margin: '0 0 6px' };

export default function CannedResponses() {
    const { user } = useContext(AuthContext);
    const isAdmin = user?.role === 'admin';
    const isStaff = ['admin', 'agent', 'leader'].includes(user?.role);

    const [items,   setItems]   = useState([]);
    const [depts,   setDepts]   = useState([]);
    const [loading, setLoading] = useState(true);

    // Crear
    const [newTitle, setNewTitle] = useState('');
    const [newBody,  setNewBody]  = useState('');
    const [newDept,  setNewDept]  = useState('');
    const [creating, setCreating] = useState(false);

    // Edición
    const [showEdit,   setShowEdit]   = useState(false);
    const [editItem,   setEditItem]   = useState(null);
    const [editTitle,  setEditTitle]  = useState('');
    const [editBody,   setEditBody]   = useState('');
    const [editDept,   setEditDept]   = useState('');
    const [editActive, setEditActive] = useState(true);
    const [saving,     setSaving]     = useState(false);

    useEffect(() => {
        const q = isAdmin ? '?all=1' : '';
        Promise.all([
            api.get(`/canned${q}`),
            isAdmin ? api.get('/departments') : Promise.resolve({ data: [] }),
        ])
            .then(([cRes, dRes]) => {
                setItems(cRes.data);
                setDepts(dRes.data || []);
            })
            .catch(err => toast.error(err.response?.data?.error || 'Error al cargar'))
            .finally(() => setLoading(false));
    }, [isAdmin]);

    const handleCreate = async e => {
        e.preventDefault();
        if (!newTitle.trim() || !newBody.trim()) {
            toast.warn('Título y cuerpo son obligatorios');
            return;
        }
        setCreating(true);
        try {
            const res = await api.post('/canned', {
                title: newTitle,
                body:  newBody,
                department: isAdmin ? (newDept || null) : null,
            });
            setItems(prev => [...prev, res.data].sort((a, b) => a.title.localeCompare(b.title)));
            setNewTitle(''); setNewBody(''); setNewDept('');
            toast.success('Respuesta rápida creada');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al crear');
        } finally { setCreating(false); }
    };

    const openEdit = item => {
        setEditItem(item);
        setEditTitle(item.title);
        setEditBody(item.body);
        setEditDept(item.department?._id || '');
        setEditActive(item.active);
        setShowEdit(true);
    };

    const handleSaveEdit = async () => {
        if (!editTitle.trim() || !editBody.trim()) {
            toast.warn('Título y cuerpo son obligatorios');
            return;
        }
        setSaving(true);
        try {
            const payload = { title: editTitle, body: editBody, active: editActive };
            if (isAdmin) payload.department = editDept || '';
            const res = await api.patch(`/canned/${editItem._id}`, payload);
            setItems(prev => prev.map(i => i._id === res.data._id ? res.data : i));
            toast.success('Actualizada');
            setShowEdit(false);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al actualizar');
        } finally { setSaving(false); }
    };

    const handleDelete = async id => {
        if (!window.confirm('¿Eliminar esta respuesta rápida?')) return;
        try {
            await api.delete(`/canned/${id}`);
            setItems(prev => prev.filter(i => i._id !== id));
            toast.info('Eliminada');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al eliminar');
        }
    };

    // Separar globales vs personales
    const globalItems   = items.filter(i => !i.isPersonal);
    const personalItems = items.filter(i => i.isPersonal);

    const canEdit = item =>
        isAdmin || (item.isPersonal && String(item.createdBy?._id || item.createdBy) === String(user?.id));

    const renderItem = item => (
        <div
            key={item._id}
            className="card-hover"
            style={{
                ...CARD,
                padding: '16px 20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 12,
                opacity: item.active ? 1 : 0.6,
            }}
        >
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <p style={{ fontWeight: 700, color: 'var(--c-text-1)', margin: 0, fontSize: 14 }}>
                        {item.title}
                    </p>
                    {!item.active && (
                        <Badge bg="secondary" style={{ fontSize: 10 }}>Inactiva</Badge>
                    )}
                    {item.department?.name && (
                        <Badge bg="info" text="dark" style={{ fontSize: 10 }}>
                            {item.department.name}
                        </Badge>
                    )}
                </div>
                <p style={{
                    color: 'var(--c-text-3)', fontSize: 13, margin: 0,
                    whiteSpace: 'pre-wrap', lineHeight: 1.5,
                    overflow: 'hidden', display: '-webkit-box',
                    WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                }}>
                    {item.body}
                </p>
            </div>
            {canEdit(item) && (
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <Button
                        size="sm" variant="outline-secondary"
                        onClick={() => openEdit(item)}
                        style={{ borderRadius: 8, fontSize: 12 }}
                    >
                        Editar
                    </Button>
                    <Button
                        size="sm" variant="outline-danger"
                        onClick={() => handleDelete(item._id)}
                        style={{ borderRadius: 8, fontSize: 12 }}
                    >
                        Eliminar
                    </Button>
                </div>
            )}
        </div>
    );

    return (
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '24px 20px' }}>
            <h4 style={{ fontWeight: 700, margin: '0 0 4px', color: 'var(--c-text-1)' }}>Respuestas Rápidas</h4>
            <p style={{ color: 'var(--c-text-3)', fontSize: 14, margin: '0 0 24px' }}>
                Plantillas de texto que puedes insertar en comentarios con un clic.
            </p>

            {/* Formulario de creación — admin y staff */}
            {isStaff && (
                <div style={{ ...CARD, padding: '20px 24px', marginBottom: 24 }}>
                    <p style={LABEL}>
                        {isAdmin ? 'Nueva respuesta rápida (global)' : 'Nueva respuesta personal'}
                    </p>
                    {!isAdmin && (
                        <p style={{ fontSize: 13, color: 'var(--c-text-4)', marginBottom: 12 }}>
                            Solo tú podrás ver y usar estas respuestas.
                        </p>
                    )}
                    <Form onSubmit={handleCreate}>
                        <Form.Group className="mb-2">
                            <Form.Control
                                type="text"
                                value={newTitle}
                                onChange={e => setNewTitle(e.target.value)}
                                placeholder="Título (ej. Saludo inicial)"
                                required
                                style={{ borderRadius: 8, fontSize: 14 }}
                            />
                        </Form.Group>
                        <Form.Group className="mb-2">
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={newBody}
                                onChange={e => setNewBody(e.target.value)}
                                placeholder="Cuerpo del mensaje…"
                                required
                                style={{ borderRadius: 8, fontSize: 14 }}
                            />
                        </Form.Group>
                        {isAdmin && (
                            <Form.Group className="mb-3">
                                <Form.Select
                                    value={newDept}
                                    onChange={e => setNewDept(e.target.value)}
                                    style={{ borderRadius: 8, fontSize: 14 }}
                                >
                                    <option value="">— Todos los departamentos —</option>
                                    {depts.map(d => (
                                        <option key={d._id} value={d._id}>{d.name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        )}
                        <Button type="submit" variant="success" disabled={creating} style={{ borderRadius: 8 }}>
                            {creating ? <Spinner size="sm" animation="border" /> : 'Crear respuesta'}
                        </Button>
                    </Form>
                </div>
            )}

            {loading ? (
                <div style={{ textAlign: 'center', padding: '48px 0' }}><Spinner animation="border" /></div>
            ) : (
                <>
                    {/* Respuestas personales (agentes/líderes) */}
                    {!isAdmin && (
                        <div style={{ marginBottom: 28 }}>
                            <p style={{ ...LABEL, marginBottom: 12 }}>Mis respuestas</p>
                            {personalItems.length === 0 ? (
                                <p style={{ color: 'var(--c-text-4)', fontSize: 13 }}>
                                    Aún no tienes respuestas personales.
                                </p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {personalItems.map(renderItem)}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Respuestas globales — solo admin */}
                    {isAdmin && (
                        <div>
                            <p style={{ ...LABEL, marginBottom: 12 }}>Todas las respuestas</p>
                            {items.length === 0 ? (
                                <p style={{ color: 'var(--c-text-4)', fontSize: 13 }}>No hay respuestas rápidas aún.</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {items.map(renderItem)}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {/* Modal de edición */}
            <Modal show={showEdit} onHide={() => setShowEdit(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontWeight: 700, fontSize: 16 }}>Editar Respuesta Rápida</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Título</Form.Label>
                            <Form.Control
                                value={editTitle}
                                onChange={e => setEditTitle(e.target.value)}
                                required
                                style={{ borderRadius: 8 }}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Cuerpo</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={5}
                                value={editBody}
                                onChange={e => setEditBody(e.target.value)}
                                required
                                style={{ borderRadius: 8 }}
                            />
                        </Form.Group>
                        {isAdmin && (
                            <Form.Group className="mb-3">
                                <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Departamento</Form.Label>
                                <Form.Select
                                    value={editDept}
                                    onChange={e => setEditDept(e.target.value)}
                                    style={{ borderRadius: 8 }}
                                >
                                    <option value="">— Todos los departamentos —</option>
                                    {depts.map(d => (
                                        <option key={d._id} value={d._id}>{d.name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        )}
                        <Form.Check
                            type="switch"
                            id="edit-active-switch"
                            label="Activa"
                            checked={editActive}
                            onChange={e => setEditActive(e.target.checked)}
                            style={{ fontSize: 14 }}
                        />
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEdit(false)} style={{ borderRadius: 8 }}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={handleSaveEdit} disabled={saving} style={{ borderRadius: 8 }}>
                        {saving ? <Spinner size="sm" animation="border" /> : 'Guardar'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
